import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DataItem, load, navigateTo } from 'scrivito'

import { StringText } from '@/Atomic/Atoms/StringText'
import { Button } from '@/Atomic/Molecules/Button'
import { CustomField } from '@/Atomic/Organisms/CustomField'
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from '@/Atomic/Templates/Drawer'
import { Form, FormBody, FormFields, FormGroup } from '@/Atomic/Templates/Form'
import { errorToast } from '@/Components/Toast'
import { Activity } from '@/Data/Activity/ActivityDataClass'
import { DataClassName } from '@/Data/const'
import { FollowUpActivity } from '@/Data/FollowUpActivity/FollowUpActivityDataClass'
import { invalidateViaRegistry } from '@/hooks/useInvalidationRegistry'
import { useDataClassFormHandler } from '@/utils/CRUD/hooks/useDataClassFormHandler'
import { getDataClassFormMap } from '@/utils/CRUD/read/dataClass'
import { getDataItem } from '@/utils/getDataItem'

interface DataClassFollowUpActivityDrawerProps {
  originActivity: DataItem
  isOpen: boolean
  close: () => void
  navigateOnCreated?: boolean
  onCreated?: (followUpActivity: DataItem) => void
}

export const DataClassFollowUpActivityDrawer: React.FC<
  DataClassFollowUpActivityDrawerProps
> = ({ originActivity, isOpen, close, navigateOnCreated, onCreated }) => {
  const { t } = useTranslation()
  const origin = originActivity.id()

  const { methods } = useDataClassFormHandler(FollowUpActivity, {
    prefill: {
      enabled: isOpen,
      payload: { entityName: 'follow-up', data: { origin } },
    },
  })
  const { getValues, watch, reset, handleSubmit } = methods
  const groups = getDataClassFormMap(FollowUpActivity, getValues, watch)

  const onDrawerClose = () => {
    reset()
    close()
  }

  const onNavigate = (id: string) => {
    load(() => getDataItem(Activity, id)).then((activity) =>
      navigateTo(activity.dataItem),
    )
  }

  const handleCreate = handleSubmit(async (data) => {
    try {
      const followUpActivity = await FollowUpActivity.create({
        ...data,
        origin,
      })

      onCreated?.(followUpActivity)
      reset()
      close()
      // Refresh the Activity caches so the follow-ups list picks up the new item immediately.
      invalidateViaRegistry(DataClassName.ACTIVITY)
      if (navigateOnCreated !== false) {
        onNavigate(followUpActivity.id())
      }
    } catch (error) {
      errorToast(
        error instanceof Error
          ? error
          : new Error(t('errors.unknownError.headline')),
      )
    }
  })

  return (
    <Drawer isOpen={isOpen} close={onDrawerClose} direction="right">
      <DrawerHeader>
        {t('components.followUpActivity.canvas', {
          title: originActivity.get('subject') as string,
        })}
        <Button
          variant="icon"
          icon="cross"
          tooltip={t('tooltip.close')}
          onClick={onDrawerClose}
        />
      </DrawerHeader>
      <DrawerBody>
        <FormProvider {...methods}>
          <Form onSubmit={handleCreate}>
            <FormBody>
              {groups.map((group) => (
                <FormGroup key={group.name}>
                  <StringText variant="title">{group.title}</StringText>
                  <FormFields>
                    {group.fields.map((field) => (
                      <CustomField
                        key={field.fieldKey}
                        fieldMap={field}
                        isEditing={true}
                        className="form-field"
                      />
                    ))}
                  </FormFields>
                </FormGroup>
              ))}
            </FormBody>
          </Form>
        </FormProvider>
      </DrawerBody>
      <DrawerFooter align="right">
        <Button variant="outlined" onClick={onDrawerClose} size="lg">
          {t('common.cancel')}
        </Button>
        <Button icon="disk" onClick={handleCreate} size="lg">
          {t('common.save')}
        </Button>
      </DrawerFooter>
    </Drawer>
  )
}
